import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, LogOut } from "lucide-react";
import { BRAND } from "../data/mock";
import { AdminLogin } from "../components/admin/AdminLogin";
import { AdminStats } from "../components/admin/AdminStats";
import { OrdersTable } from "../components/admin/OrdersTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchOrders = useCallback(async (accessCode) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/orders`, {
        headers: { "X-Admin-Code": accessCode },
      });
      setData(res.data);
      sessionStorage.setItem("t4r_admin_code", accessCode);
      setCode(accessCode);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Invalid access code.");
        sessionStorage.removeItem("t4r_admin_code");
        setCode("");
      } else {
        toast.error("Could not load orders.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const stored = sessionStorage.getItem("t4r_admin_code");
    if (stored) fetchOrders(stored);
  }, [fetchOrders]);

  const logout = () => {
    sessionStorage.removeItem("t4r_admin_code");
    setCode("");
    setData(null);
  };

  const resendDelivery = async (order) => {
    const email = window.prompt("Send the PDF to this email (edit if the customer typo'd it):", order.email);
    if (!email) return;
    try {
      const res = await axios.post(
        `${API}/admin/orders/${order.session_id}/resend`,
        { email: email.trim() },
        { headers: { "X-Admin-Code": code } }
      );
      if (res.data.sent) {
        toast.success(`Delivery email sent to ${email.trim()}`);
      } else {
        toast.error(res.data.error || "Send failed — check the address and try again.");
      }
      fetchOrders(code);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resend the email.");
    }
  };

  const copyDownloadLink = async (order) => {
    const link = `${window.location.origin}/?session_id=${order.session_id}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Download link copied — send it to the customer, their PDF download appears when they open it.");
    } catch {
      window.prompt("Copy this download link and send it to the customer:", link);
    }
  };

  if (!code || !data) {
    return <AdminLogin onSubmit={fetchOrders} loading={loading} />;
  }

  return (
    <div className="min-h-screen bg-[#050608] text-white" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl px-4 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2 font-heading text-lg">
            <img src={BRAND.logoUrl} alt="logo" className="h-8 w-auto object-contain" />
            {BRAND.logoFirst}<span className="text-[#2f7fff]">{BRAND.logoAccent}</span>{BRAND.logoSecond}
            <span className="ml-2 rounded-full border border-white/15 px-3 py-1 text-xs font-sans not-italic text-zinc-400">Orders</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchOrders(code)}
              disabled={loading}
              className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 px-4 py-2 text-sm text-white transition-colors"
              data-testid="admin-refresh-button"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 px-4 py-2 text-sm text-zinc-400 transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-4 w-4" /> Lock
            </button>
          </div>
        </div>

        <AdminStats stats={data.stats} />
        <OrdersTable orders={data.orders} onResend={resendDelivery} onCopyLink={copyDownloadLink} />
      </div>
    </div>
  );
}
